import { supabase } from './supabase';
import { logger } from './logger';
import type { StatCardData, ActivityEvent } from '../types';

export async function fetchDashboardStats(): Promise<{ row1: StatCardData[]; row2: StatCardData[] }> {
  const [
    { count: candidateCount },
    { count: employerCount },
    { count: activeOffers },
    { count: applicationCount },
    { count: pendingVideos },
    { data: subscriptions },
  ] = await Promise.all([
    // Compté depuis `profiles` : un compte sans profil métier reste un inscrit.
    supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'candidate'),
    supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'employer'),
    supabase.from('job_offers').select('*', { count: 'exact', head: true }).eq('status', 'active'),
    supabase.from('applications').select('*', { count: 'exact', head: true }),
    supabase.from('candidate_videos').select('*', { count: 'exact', head: true }).eq('status', 'pending_review'),
    supabase.from('subscriptions').select('amount_ht, status, plan'),
  ]);

  const totalUsers = (candidateCount || 0) + (employerCount || 0);
  // Seul le plan payant produit du revenu : les lignes 'free' sont du suivi.
  const activePaidSubs = (subscriptions || []).filter((s: any) => s.status === 'active' && s.plan === 'premium');
  const freeSubs = (subscriptions || []).filter((s: any) => s.plan !== 'premium');
  const mrr = activePaidSubs.reduce((sum: number, s: any) => sum + (s.amount_ht || 0), 0);

  const row1: StatCardData[] = [
    { label: 'Total inscrits', value: String(totalUsers), subtitle: `${candidateCount || 0} candidats + ${employerCount || 0} recruteurs`, icon: '👥', color: 'green', link: '/candidates' },
    { label: 'Candidats', value: String(candidateCount || 0), subtitle: 'profils enregistrés', icon: '🧑‍💼', color: 'blue', link: '/candidates' },
    { label: 'Recruteurs', value: String(employerCount || 0), subtitle: `dont ${freeSubs.length} en gratuit`, icon: '🏢', color: 'purple', link: '/employers' },
    { label: 'MRR', value: `${mrr.toLocaleString('fr-FR')} €`, subtitle: 'revenu mensuel', icon: '💰', color: 'teal', link: '/subscriptions' },
  ];

  const row2: StatCardData[] = [
    { label: 'Offres actives', value: String(activeOffers || 0), subtitle: 'publiées', icon: '📋', color: 'orange', link: '/offers' },
    { label: 'Candidatures', value: String(applicationCount || 0), subtitle: 'total', icon: '📨', color: 'blue', link: '/offers' },
    { label: 'Vidéos en attente', value: String(pendingVideos || 0), subtitle: 'à modérer', icon: '🎬', color: 'red', link: '/videos' },
    { label: 'Comptes gratuits', value: String(freeSubs.length), subtitle: 'modèle publicitaire', icon: '🆓', color: 'purple', link: '/subscriptions' },
  ];

  logger.technical('fetch_dashboard_stats_ok', 'statsService', {
    totalUsers,
    activeOffers: activeOffers || 0,
    pendingVideos: pendingVideos || 0,
    mrr,
  });

  return { row1, row2 };
}

export async function fetchRecentActivity(): Promise<ActivityEvent[]> {
  const events: ActivityEvent[] = [];

  const { data: recentApps } = await supabase
    .from('applications')
    .select('id, applied_at, candidate_profiles!applications_candidate_id_fkey ( first_name, last_name )')
    .order('applied_at', { ascending: false })
    .limit(5);

  (recentApps || []).forEach((a: any) => {
    const name = `${a.candidate_profiles?.first_name || ''} ${a.candidate_profiles?.last_name || ''}`.trim();
    events.push({
      id: `app-${a.id}`,
      type: 'candidature',
      description: 'Candidature envoyée',
      user: name || 'Candidat',
      date: new Date(a.applied_at).toLocaleDateString('fr-FR'),
      time: new Date(a.applied_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      // Une candidature renvoie vers son détail (profil + annonce + compatibilité).
      link: `/applications/${a.id}`,
    });
  });

  const { data: recentVideos } = await supabase
    .from('candidate_videos')
    .select('id, created_at, candidate_profiles!candidate_videos_candidate_id_fkey ( first_name, last_name )')
    .order('created_at', { ascending: false })
    .limit(5);

  (recentVideos || []).forEach((v: any) => {
    const name = `${v.candidate_profiles?.first_name || ''} ${v.candidate_profiles?.last_name || ''}`.trim();
    events.push({
      id: `vid-${v.id}`,
      type: 'video',
      description: 'Vidéo soumise',
      user: name || 'Candidat',
      date: new Date(v.created_at).toLocaleDateString('fr-FR'),
      time: new Date(v.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      // Une vidéo soumise renvoie vers la modération (focus sur la vidéo).
      link: `/videos?focus=${v.id}`,
    });
  });

  const { data: recentOffers } = await supabase
    .from('job_offers')
    .select('id, created_at, position_name, employer_profiles!job_offers_employer_id_fkey ( company_name )')
    .order('created_at', { ascending: false })
    .limit(5);

  (recentOffers || []).forEach((o: any) => {
    events.push({
      id: `off-${o.id}`,
      type: 'offre',
      description: o.position_name ? `Offre publiée · ${o.position_name}` : 'Offre publiée',
      user: o.employer_profiles?.company_name || 'Recruteur',
      date: new Date(o.created_at).toLocaleDateString('fr-FR'),
      time: new Date(o.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      link: `/offers?focus=${o.id}`,
    });
  });

  events.sort((a, b) => {
    const da = new Date(`${a.date.split('/').reverse().join('-')} ${a.time}`);
    const db = new Date(`${b.date.split('/').reverse().join('-')} ${b.time}`);
    return db.getTime() - da.getTime();
  });

  const result = events.slice(0, 10);
  logger.technical('fetch_recent_activity_ok', 'statsService', { count: result.length });
  return result;
}
